<template>
  <footer id="footer">
    <div class="footer-accent" aria-hidden="true"></div>

    <div class="footer-main">
      <div class="footer-inner">
        <div class="footer-brand">
          <img
            class="footer-logo"
            loading="lazy"
            decoding="async"
            src="../assets/logoWhite-clean.png"
            alt="GYMPODS"
            width="744"
            height="966"
          />
          <p class="footer-tagline">
            Private pay-as-you-go gym spaces in Dalston &amp; Putney. Train solo,
            with a buddy, or with a personal trainer &mdash; no contract, no crowd.
          </p>
          <div class="footer-socials" aria-label="GYMPODS on social media">
            <a
              href="https://instagram.com/gympods/"
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconInstagram :size="18" />
            </a>
            <a
              href="https://www.facebook.com/gympods"
              aria-label="Facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconFacebook :size="18" />
            </a>
            <a
              href="https://www.tiktok.com/@gympods"
              aria-label="TikTok"
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconTikTok :size="18" />
            </a>
          </div>
        </div>

        <nav class="footer-links" aria-label="Footer">
          <h3 class="footer-heading">Explore</h3>
          <ul>
            <li><router-link to="/">Home</router-link></li>
            <li><router-link to="/dalston">GYMPODS Dalston</router-link></li>
            <li><router-link to="/putney">GYMPODS Putney</router-link></li>
            <li><router-link to="/personal-training">Personal Training</router-link></li>
            <li><router-link to="/pricing">Pricing</router-link></li>
            <li><router-link to="/faqs">FAQs</router-link></li>
            <li><router-link to="/franchise">Franchise</router-link></li>
            <li><router-link to="/gym-space-to-rent">For Trainers &amp; Therapists</router-link></li>
            <li><router-link to="/contact">Contact</router-link></li>
          </ul>
        </nav>

        <div class="footer-newsletter">
          <h3 class="footer-heading">Stay in the loop</h3>
          <p class="footer-newsletter-sub">
            Offers, new POD launches and training tips &mdash; straight to your
            inbox.
          </p>
          <form
            class="newsletter-form"
            @submit.prevent="submitNewsletter"
            novalidate
          >
            <label for="footer-newsletter-email" class="sr-only"
              >Email address</label
            >
            <input
              id="footer-newsletter-email"
              v-model="newsletter.email"
              type="email"
              placeholder="you@email.com"
              autocomplete="email"
              :disabled="
                newsletter.status === 'submitting' ||
                newsletter.status === 'success'
              "
            />
            <button
              type="submit"
              class="cta-button"
              :disabled="
                newsletter.status === 'submitting' ||
                newsletter.status === 'success'
              "
            >
              <span v-if="newsletter.status === 'submitting'">Signing up&hellip;</span>
              <span v-else-if="newsletter.status === 'success'">Subscribed</span>
              <span v-else>Sign up <ArrowRight :size="14" /></span>
            </button>
          </form>
          <p
            v-if="newsletter.status === 'invalid'"
            class="newsletter-msg newsletter-msg--error"
            role="alert"
          >
            Enter a valid email address.
          </p>
          <p
            v-else-if="newsletter.status === 'error'"
            class="newsletter-msg newsletter-msg--error"
            role="alert"
          >
            We couldn&rsquo;t send that. Please email
            <a href="mailto:info@gympods.com">info@gympods.com</a>.
          </p>
          <p
            v-else-if="newsletter.status === 'success'"
            class="newsletter-msg newsletter-msg--success"
          >
            Thanks &mdash; you&rsquo;re on the list.
          </p>

          <h3 class="footer-heading footer-heading--app">Get the app</h3>
          <div class="footer-apps">
            <a
              href="https://play.google.com/store/apps/details?id=com.gympods.app"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
            >
              <picture>
                <source type="image/webp" srcset="/img/googleplay.webp" />
                <img
                  alt="Get it on Google Play"
                  loading="lazy"
                  decoding="async"
                  src="/img/googleplay.png"
                  width="480"
                  height="143"
                />
              </picture>
            </a>
            <a
              href="https://apps.apple.com/gb/app/gympods/id6502242401"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
            >
              <picture>
                <source type="image/webp" srcset="/img/appstore.webp" />
                <img
                  loading="lazy"
                  decoding="async"
                  src="/img/appstore.png"
                  alt="Download on the App Store"
                  width="480"
                  height="142"
                />
              </picture>
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-base">
      <div class="footer-base-inner">
        <p class="footer-copyright">
          &copy; 2026 GYMPODS &middot; The FIT POD Company Ltd
          <span class="footer-credit"><span class="footer-credit-sep" aria-hidden="true">&middot; </span>Website built by <a href="https://veylmarketing.com" target="_blank" rel="noopener" aria-label="Veyl Marketing (opens in new tab)">Veyl Marketing</a></span>
        </p>
        <ul class="footer-legal">
          <li><router-link to="/terms">Terms &amp; Conditions</router-link></li>
          <li><router-link to="/privacy">Privacy Policy</router-link></li>
          <li><router-link to="/cookies">Cookie Policy</router-link></li>
          <li><a href="#" @click.prevent="openCookieSettings">Manage cookies</a></li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script>
import { ArrowRight } from 'lucide-vue-next';
import IconFacebook from '@/components/icons/IconFacebook.vue';
import IconInstagram from '@/components/icons/IconInstagram.vue';
import IconTikTok from '@/components/icons/IconTikTok.vue';
import { sendContact } from '@/app/routes.js';

export default {
  name: 'PageFooter',
  components: { ArrowRight, IconFacebook, IconInstagram, IconTikTok },
  data() {
    return {
      newsletter: {
        email: '',
        status: 'idle',
      },
    };
  },
  methods: {
    async submitNewsletter() {
      const email = this.newsletter.email.trim();
      const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
      if (!emailValid) {
        this.newsletter.status = 'invalid';
        return;
      }
      this.newsletter.status = 'submitting';
      const result = await sendContact(
        'Newsletter signup',
        email,
        'Newsletter signup from site footer.'
      );
      if (result === 'success') {
        this.newsletter.status = 'success';
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'newsletter_signup' });
        this.newsletter.email = '';
      } else {
        // 'invalid' or 'error'; the email stays in the box.
        this.newsletter.status = result;
      }
    },
    openCookieSettings() {
      if (window.klaro && typeof window.klaro.show === 'function') {
        window.klaro.show();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$aqua: #7fc0c3;
$charcoal: #231F20;
$carbon: #58595B;
$zest: #D8F789;
$midnight: #193446;
$midnight-deep: #0f2433;

#footer {
  width: 100%;
  color: #fff;
  background: linear-gradient(180deg, $midnight 0%, $midnight-deep 100%);
  font-family: inherit;
  text-transform: none;
  letter-spacing: normal;
}

.footer-accent {
  height: 4px;
  background: linear-gradient(90deg, $aqua 0%, $zest 100%);
}

.footer-main {
  padding: 72px 0 56px;
}

.footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 5%;
  display: grid;
  grid-template-columns: 1.3fr 1fr 1.4fr;
  gap: 64px;
  box-sizing: border-box;
}

.footer-brand {
  display: flex;
  flex-direction: column;
  text-align: left;

  .footer-logo {
    width: 180px;
    height: auto;
    margin-bottom: 22px;
  }

  .footer-tagline {
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.95rem;
    line-height: 1.65;
    margin: 0 0 26px;
    max-width: 340px;
  }
}

.footer-socials {
  display: flex;
  gap: 12px;

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 1.05rem;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease,
      border-color 0.2s ease, transform 0.2s ease;

    i {
      margin: 0;
    }

    &:hover,
    &:focus-visible {
      background: $zest;
      color: $midnight;
      border-color: $zest;
      transform: translateY(-2px);
      text-shadow: none;
    }

    &:focus-visible {
      outline: 2px solid $aqua;
      outline-offset: 3px;
    }
  }
}

.footer-heading {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: $aqua;
  margin: 0 0 20px;
}

.footer-heading--app {
  margin-top: 34px;
}

.footer-links {
  text-align: left;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  a {
    color: rgba(255, 255, 255, 0.82);
    text-decoration: none;
    font-size: 0.95rem;
    transition: color 0.2s ease;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: $zest;
      text-shadow: none;
    }

    &:focus-visible {
      outline: 2px solid $aqua;
      outline-offset: 3px;
      border-radius: 2px;
    }
  }
}

.footer-newsletter {
  text-align: left;

  .footer-newsletter-sub {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    line-height: 1.55;
    margin: 0 0 16px;
    max-width: 400px;
  }
}

.newsletter-form {
  display: flex;
  gap: 8px;
  max-width: 400px;

  input {
    flex: 1;
    min-width: 0;
    padding: 12px 14px;
    font-size: 0.95rem;
    color: #fff;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s ease, background 0.2s ease;
    font-family: inherit;

    &::placeholder {
      color: rgba(255, 255, 255, 0.45);
    }

    &:focus {
      border-color: $aqua;
      background: rgba(255, 255, 255, 0.1);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .cta-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: $zest;
    color: $midnight;
    border: none;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease,
      box-shadow 0.2s ease;
    white-space: nowrap;
    font-family: inherit;

    &:hover:not(:disabled) {
      background: #e8ffa6;
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(222, 245, 149, 0.2);
    }

    &:focus-visible {
      outline: 2px solid $aqua;
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.75;
      cursor: not-allowed;
    }

    i {
      font-size: 0.8rem;
      margin: 0;
    }
  }
}

.newsletter-msg {
  margin: 12px 0 0;
  font-size: 0.85rem;
  line-height: 1.4;

  &--error {
    color: #ffc2c2;

    a {
      color: $zest;
      text-decoration: underline;
    }
  }

  &--success {
    color: $zest;
  }
}

.footer-apps {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  a {
    display: inline-block;
    transition: transform 0.2s ease, opacity 0.2s ease;

    &:hover,
    &:focus-visible {
      transform: translateY(-2px);
      opacity: 0.92;
      text-shadow: none;
    }

    &:focus-visible {
      outline: 2px solid $aqua;
      outline-offset: 3px;
      border-radius: 6px;
    }

    img {
      width: 140px;
      height: auto;
      display: block;
      margin: 0;
    }
  }
}

.footer-base {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.22);
}

.footer-base-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 22px 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  box-sizing: border-box;
}

.footer-copyright {
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.82rem;
  letter-spacing: 0.02em;
  // #app is text-align: center; keep both lines flush-left on mobile.
  text-align: left;
}

// Kept on one line so the credit wraps as a unit on narrow screens.
.footer-credit {
  white-space: nowrap;

  a {
    color: rgba(255, 255, 255, 0.85);
    text-decoration: underline;
    text-decoration-color: rgba(255, 255, 255, 0.35);
    text-underline-offset: 3px;
    transition: color 0.2s ease, text-decoration-color 0.2s ease;

    &:hover,
    &:focus-visible {
      color: $zest;
      text-decoration-color: $zest;
    }

    &:focus-visible {
      outline: 2px solid $aqua;
      outline-offset: 3px;
      border-radius: 2px;
    }
  }
}

.footer-legal {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  a {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.82rem;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover,
    &:focus-visible {
      color: $zest;
      text-shadow: none;
    }

    &:focus-visible {
      outline: 2px solid $aqua;
      outline-offset: 3px;
      border-radius: 2px;
    }
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
  padding: 0;
  margin: -1px;
}

@media (max-width: 960px) {
  .footer-inner {
    grid-template-columns: 1fr 1fr;
    gap: 44px;
  }

  .footer-newsletter {
    grid-column: 1 / -1;
  }
}

@media (max-width: 600px) {
  .footer-main {
    padding: 52px 0 36px;
  }

  .footer-inner {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .footer-brand .footer-logo {
    width: 150px;
  }

  .footer-heading--app {
    margin-top: 28px;
  }

  .newsletter-form {
    flex-direction: column;
    gap: 10px;
    max-width: 100%;
  }

  .newsletter-form .cta-button {
    width: 100%;
  }

  .footer-apps img {
    width: 130px;
  }

  .footer-base-inner {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 5%;
  }

  .footer-legal {
    gap: 18px;
  }

  // Credit gets its own line on phones, without a dangling separator.
  .footer-credit {
    display: block;
    margin-top: 4px;
  }

  .footer-credit-sep { display: none; }
}
</style>
